import React, { useState } from 'react';
import Button from '../../../../components/button/Button';
import { ExportTwoIcon, RefreshCircleIcon } from '../../../../components/icons';
import CustomTable from '../../../../components/CustomTable';
import { formatDate, formatNumber } from '../../../../utils/helper';
import FilterTreasuryTransactions from './FilterTreasuryTransactions';
import { FilterParams } from '../../../../interfaces/Filter';
import TransactionTableMobileCard from '../TransactionsDetails/Components/TransactionTableMobileCard';
import { useTreasuryTransactions } from '../../../../customhooks/treasury/useTreasuryTransactions';
import { Avatar } from 'antd';
import NoTransactionsIcon from '../../../../components/icons/NoTransactionsIcon';
import { getBanks } from 'banks-ng';
import TreasuryTransactionDetails from './TreasuryTransactionDetails';
import { TreasuryTransaction } from '../../../../interfaces/Treasury';
import { Bank } from '../../../../customhooks/useBankLogos';

const TreasuryTable = () => {
    const [viewDetails, setViewDetails] = useState(false);
    const [
        selectedTransaction,
        setSelectedTransaction
    ] = useState<TreasuryTransaction | null>(null);

    const [transactionFilterOptions, setTransactionFilterOptions] = useState<
        FilterParams
    >({
        page: 0,
        size: 20
    });

    const {
        isLoading: isTransactionsLoading,
        transactionsList,
        invalidateTransactionsCache: fetchTransactions,
        isFetching: isFetchingTransactions,
        transactionPaging
    } = useTreasuryTransactions(null, transactionFilterOptions);

    const noTransactions =
        !transactionsList?.transactions?.length &&
        !isFetchingTransactions &&
        !isTransactionsLoading;

    const handleViewTransactions = (record: TreasuryTransaction) => {
        if (record) {
            setSelectedTransaction(record);
            setViewDetails(true);
        }
    };

    const BankResult: Bank[] = getBanks();

    const bankLookupByName = React.useMemo(() => {
        const map = new Map<string, Bank>();
        BankResult.forEach((bank: Bank) => {
            map.set(bank.name.toLowerCase(), bank);
        });
        return map;
    }, [BankResult]);

    return (
        <>
            {noTransactions ? (
                <div className='w-full flex justify-center items-center lg:h-[80vh] h-[80dvh] bg-white lg:bg-transparent flex-col gap-2'>
                    <NoTransactionsIcon />
                    <h3 className='text-[20px] font-semibold text-center text-ebony'>
                        No transaction data available
                    </h3>
                    <Button
                        text='Refresh Transactions'
                        className='mt-3'
                        size='sm'
                        isLoading={
                            isFetchingTransactions || isTransactionsLoading
                        }
                        leftIcon={<RefreshCircleIcon color='#ffffff' />}
                        onClick={fetchTransactions}
                    />
                </div>
            ) : (
                <div className='bg-white rounded-[8px] overflow-hidden'>
                    <div className='flex justify-between items-center px-[24px] py-[20px]'>
                        <h4 className='hidden lg:block text-ebony font-semibold text-lg leading-[28px]'>
                            All transactions
                        </h4>

                        <div className='hidden lg:flex gap-2'>
                            <Button
                                leftIcon={<ExportTwoIcon />}
                                text='Export CSV'
                                variant='secondaryOutline'
                                size='sm'
                            />
                            <FilterTreasuryTransactions
                                {...{
                                    transactionFilterOptions,
                                    setTransactionFilterOptions
                                }}
                            />
                            <Button
                                leftIcon={<RefreshCircleIcon />}
                                text='Refresh Transactions'
                                className='font-medium text-ebony'
                                onClick={fetchTransactions}
                                size='sm'
                                variant='tertiary'
                            />
                        </div>
                    </div>

                    <CustomTable
                        data={transactionsList?.transactions ?? []}
                        loadingData={
                            isTransactionsLoading || isFetchingTransactions
                        }
                        updateCurrentData={(record) =>
                            handleViewTransactions(record)
                        }
                        onMobileRowClick={(record) =>
                            handleViewTransactions(record)
                        }
                        columns={[
                            {
                                title: 'Bank',
                                dataIndex: 'bankName',
                                key: 'bankName',
                                render: (_, { bankName }) => {
                                    const bank = bankLookupByName.get(
                                        bankName.toLowerCase()
                                    );

                                    return (
                                        <div className='flex gap-3 items-center'>
                                            <Avatar
                                                size='default'
                                                src={bank?.logo}
                                                style={{
                                                    border: '1px solid #D0D5DD',
                                                    background: 'transparent'
                                                }}
                                            />
                                            <p>{bankName}</p>
                                        </div>
                                    );
                                }
                            },
                            {
                                title: 'Account Number',
                                dataIndex: 'accountNumber',
                                key: 'accountNumber'
                            },
                            {
                                title: 'Amount',
                                dataIndex: 'amount',
                                key: 'amount',
                                render: (text) => (
                                    <span>
                                        NGN {formatNumber(parseFloat(text))}
                                    </span>
                                )
                            },
                            {
                                title: 'Balance after transaction',
                                dataIndex: 'balance',
                                key: 'balance',
                                render: (text) => (
                                    <span>
                                        NGN {formatNumber(parseFloat(text))}
                                    </span>
                                )
                            },
                            {
                                title: 'Narration',
                                dataIndex: 'narration',
                                key: 'narration',
                                render: (text) => (
                                    <div className='max-w-sm text-xs'>
                                        {text}
                                    </div>
                                )
                            },
                            {
                                title: 'Reference',
                                dataIndex: 'reference',
                                key: 'reference'
                            },
                            {
                                title: 'Type',
                                dataIndex: 'type',
                                key: 'type',
                                render: (text) => (
                                    <div className='capitalize'>{text}</div>
                                )
                            },
                            {
                                title: 'Date',
                                dataIndex: 'createdAt',
                                key: 'createdAt',
                                render: (_, { createdAt }) => (
                                    <span>{formatDate(createdAt)}</span>
                                )
                            }
                        ]}
                        showPagination
                        mobileComponent={<TransactionTableMobileCard />}
                        paging={{
                            ...transactionFilterOptions,
                            total: transactionPaging.total
                        }}
                        updatePage={(value) =>
                            setTransactionFilterOptions((prev) => ({
                                ...prev,
                                page: value
                            }))
                        }
                    />
                </div>
            )}
            {viewDetails && selectedTransaction && (
                <TreasuryTransactionDetails
                    open={viewDetails}
                    onClose={() => setViewDetails(false)}
                    details={selectedTransaction}
                />
            )}
        </>
    );
};

export default TreasuryTable;
